// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { XngCustomGroup } from "@tps/XDL/constants";
import { color } from "@tps/XDL/Utilities";
import React, { FC, useContext, useMemo } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    cartState: getCartState(state, state.pages.orderConfirmation.cartId),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;
export interface HaveQuestionsStyles {
    wrapper?: InjectableCss;
    contactInfoWrapper?: InjectableCss;
    contactName?: TypographyPresentationProps;
    contactInfo?: TypographyPresentationProps;
}

export const haveQuestionsStyles: HaveQuestionsStyles = {
    wrapper: {
        css: css`
            padding: 16px;
            border: 1px solid ${color("common.border")};
            @media print {
                margin-top: 16px;
            }
        `,
    },
    contactInfoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
        `,
    },
    contactName: {
        size: 14,
        lineHeight: "22px",
    },
    contactInfo: {
        size: 14,
        lineHeight: "22px",
        weight: "bold",
        color: "primary",
    },
};

const styles = haveQuestionsStyles;

const HaveQuestions: FC<Props> = ({ cartState }) => {
    const { value: order } = useContext(OrderStateContext);

    const warehouseContactInfo = useMemo(() => {
        const cartWarehouse = cartState?.value?.cartLines?.[0]?.properties?.warehouse;
        const orderWarehouse = order?.properties?.warehouse;
        const warehouseRaw = cartWarehouse || orderWarehouse;
        if (!warehouseRaw) {
            return null;
        }
        const parsed = JSON.parse(warehouseRaw);
        return cartWarehouse ? parsed : Object.values(parsed)?.[0];
    }, [cartState, order]);

    if (!warehouseContactInfo) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography variant="h5" forwardAs="p">{translate("Have Questions On Your Purchase?")}</Typography>
            {warehouseContactInfo && (
                <StyledWrapper {...styles.contactInfoWrapper}>
                    {warehouseContactInfo?.ContactName && (
                        <Typography {...styles.contactName}>{warehouseContactInfo.ContactName}</Typography>
                    )}
                    {warehouseContactInfo?.Phone && (
                        <Typography {...styles.contactInfo}>{warehouseContactInfo.Phone}</Typography>
                    )}
                    {warehouseContactInfo?.Email && (
                        <Typography {...styles.contactInfo}>{warehouseContactInfo.Email}</Typography>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(HaveQuestions),
    definition: {
        group: XngCustomGroup,
        displayName: "Have Questions?",
    },
};

export default widgetModule;
